'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import DownloadIcon from '@/components/icons/download'
import EyeCon from '@/components/icons/eye'
import LiveryDesignCTA from '@/components/marketplace/cta/livery-design-cta'
import LiveryBuyButton from '@/components/marketplace/purchase/buy-button'
import TrackedLink from '@/components/utils/tracking/tracked-link'
import { getCurrencySymbol } from '@/helpers/currency'
import { formatLiveryUpdatedDate } from '@/helpers/datetime'
import getUserName from '@/helpers/get-user-name'
import { htmlClassName, normalizeHtml } from '@/helpers/html'
import { urlSafeProfilePath } from '@/helpers/url-safe-profile-path'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWRImmutable from 'swr/immutable'
import ButtonCarousel from './button-carousel'
import LiveryOwnersOtherDesigns from './livery-owners-other-designs'

export default function Livery({ livery, session }) {
  const [buttonSticky, setButtonSticky] = useState(false)
  const updatedDate = useMemo(
    () => formatLiveryUpdatedDate(livery?.updateTs),
    [livery?.updateTs],
  )
  const ownerLiveriesQuery = useMemo(() => {
    return `/api/internal/search/liveries?creatorUserId=${encodeURIComponent(
      livery.userId,
    )}&orderBy=most-recent&page=1&pageSize=24`
  }, [livery.userId])

  const { data: ownerLiveryResponse, isLoading: ownersLiveryDesignsLoading } =
    useSWRImmutable(ownerLiveriesQuery, async (url) => {
      const response = await window.fetch(url)

      if (!response.ok) {
        throw new Error('Failed to load owner liveries')
      }

      return response.json()
    })
  const ownersLiveryDesigns = ownerLiveryResponse?.liveryDesigns || []

  useEffect(() => {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 0) {
        setButtonSticky(true)
      } else if (window.scrollY < 50) {
        setButtonSticky(false)
      }
    })
  }, [])

  const handleLiveryView = useCallback(async () => {
    let liveryViewLogs = []

    try {
      liveryViewLogs = JSON.parse(localStorage.getItem('liveryViewLog')) || []
    } catch {
      liveryViewLogs = []
    }

    if (
      Date.now() -
        liveryViewLogs?.find((viewLog) => viewLog.liveryId === livery.id)
          ?.time <
      1000 * 60 * 60
    ) {
      return
    }

    const updatedLogs = [
      ...liveryViewLogs,
      { liveryId: livery.id, time: Date.now() },
    ]
    try {
      localStorage.setItem('liveryViewLog', JSON.stringify(updatedLogs))
    } catch (error) {
      console.warn('Failed to save livery view log to localStorage', error)
    }

    const useragent = typeof window !== 'undefined' ? navigator.userAgent : null

    const geolocation =
      typeof window !== 'undefined' ? navigator.geolocation : null

    let ip = 'UNKNOWN'

    try {
      const ipAddressRes = await window.fetch(
        'https://api.ipify.org?format=json',
      )
      const ipAddress = await ipAddressRes.json()
      ip = ipAddress.ip || 'UNKNOWN'
    } catch {
      ip = 'UNKNOWN'
    }

    const postData = {
      url: `livery-design/${livery.id}/view-log`,
      data: {
        useragent,
        geolocation,
        ip,
        metadata: {},
      },
      method: 'POST',
    }

    // If we have a user send it with the post data.
    const sessionUserId = session?.user?.id
    if (sessionUserId) {
      postData.data.userId = sessionUserId
    }

    try {
      await apiFetch(postData)
    } catch (error) {
      console.warn('Failed to log livery view', error)
    }
  }, [livery.id, session?.user?.id])

  useEffect(() => {
    handleLiveryView()
  }, [handleLiveryView])

  return (
    <>
      <div className="screen-grid relative last:mb-[96px]">
        <ButtonCarousel
          showMobileDots
          className="no-grid-padding col-span-full desktop-xs:hidden"
        >
          {livery.files
            .filter((file) => file.usageHint === 'user-livery-image')
            .map((file, index) => (
              <div
                key={`carousel-item-${index}`}
                className="relative min-h-[56.25vw] w-screen shrink-0"
              >
                <Image src={file.fileUrl} alt="" fill></Image>
              </div>
            ))}
        </ButtonCarousel>
        <div className="sticky top-0 col-span-4 mb-4 mt-6 flex flex-col gap-4 max-desktop-xs:hidden desktop-sm:col-span-7">
          {livery.files
            .filter((file) => file.usageHint === 'user-livery-image')
            .sort(
              (a, b) => a.metadata?.orderingIndex - b.metadata?.orderingIndex,
            )
            .map((file, index) => (
              <div
                className="relative aspect-video w-full overflow-hidden rounded-xl"
                key={`livery-image-${index}`}
              >
                <Image
                  src={file.fileUrl}
                  alt=""
                  fill
                  className="h-full w-full object-cover"
                ></Image>
              </div>
            ))}
        </div>
        <div className="relative col-span-full mt-7 desktop-xs:col-span-4 desktop-sm:col-span-5">
          <div className="sticky top-[80px] z-50 pb-4 desktop-xs:z-auto">
            <div className="desktop-xs:rounded-md desktop-xs:bg-neutral-blue-800 desktop-xs:px-[60px] desktop-xs:py-[45px]">
              <div className="flex flex-wrap items-center gap-2">
                {livery.appliedDiscount && (
                  <div className="semibold-caps rounded-full bg-error px-3 py-2 text-footer">
                    sale
                  </div>
                )}
                {/* TODO @Nikhil TRENDING*/}
                {livery.isTrending && (
                  <div className="semibold-caps bg-gf-gradient rounded-full px-3 py-2 text-footer">
                    trending
                  </div>
                )}
              </div>
              <div className="mt-6 flex items-center gap-1">
                <DownloadIcon className="h-3 w-3 stroke-neutral-blue-500"></DownloadIcon>
                <p className="text-footer font-bold text-neutral-blue-500">
                  {livery._stats.downloads?.total
                    ? livery._stats.downloads?.total
                    : 0}
                </p>
                <EyeCon
                  className={'h-4 w-4 stroke-neutral-blue-500 stroke-2'}
                ></EyeCon>
                <p className="text-footer font-bold text-neutral-blue-500">
                  {livery._stats.views?.total ? livery._stats.views?.total : 0}
                </p>
              </div>
              <h4 className="mt-3 text-h-4 font-semibold">{livery.name}</h4>
              {updatedDate && (
                <p className="mt-2 text-footer font-semibold text-neutral-blue-500">
                  Updated {updatedDate}
                </p>
              )}
              <div className="mt-3 flex flex-col gap-1">
                <p className="text-p-2 uppercase text-neutral-blue-500">
                  {[
                    ...livery.platforms.map((p) => p.nicename),
                    livery.game.nicename,
                  ].join(' • ')}
                  {livery?.carClass && ` • ${livery.carClass}`}
                </p>
              </div>
              <div className="mt-3 flex items-center gap-1">
                <p
                  className={`text-h-5-sm font-bold ${
                    livery.currentPrice === 0
                      ? 'text-success'
                      : livery.appliedDiscount?.amount > 0
                        ? 'text-error'
                        : 'text-shades-0'
                  }`}
                >
                  {livery.currentPrice > 0
                    ? `${getCurrencySymbol(livery.currencyId)}${livery.currentPrice}`
                    : 'Free'}
                </p>
                {livery.appliedDiscount && (
                  <p className="text-[12px] text-neutral-blue-500 line-through">
                    {getCurrencySymbol(livery.currencyId)}
                    {livery.appliedDiscount.priceBeforeDiscount}
                  </p>
                )}
              </div>
              {/* Mobile created by section */}
              <div className="col-span-full mt-6 flex items-center justify-between rounded-sm bg-neutral-blue-800 py-2 max-desktop-xs:px-5 desktop-xs:hidden">
                <p className="semibold-caps text-footer text-neutral-blue-500">
                  Created By
                </p>
                <div className="flex items-center gap-2">
                  <div className="relative flex h-8 w-8 overflow-hidden rounded-full">
                    <ImageWithFallback
                      src={getUserProfilePictureUrl(livery.user, {
                        profile: 'livery',
                      })}
                      id={livery.user.id}
                      fill
                    />
                  </div>
                  <p className="text-[10px] underline">
                    {getUserName(
                      { user: livery.user },
                      {
                        profile: 'livery',
                      },
                    )}
                  </p>
                </div>
              </div>
              <div
                className={cn(
                  'no-grid-padding col-span-full mt-5 inline-flex items-center justify-center gap-2 rounded-sm border border-transparent bg-transparent px-4 py-2 text-center text-sm font-medium normal-case transition-all transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60 max-desktop-xs:w-screen desktop-xs:hidden',
                  {
                    'bottom-3 drop-shadow-[0_4px_8px_rgba(0,0,0,0.25)] max-desktop-xs:fixed':
                      buttonSticky,
                    relative: !buttonSticky,
                  },
                )}
              >
                <LiveryBuyButton livery={livery} session={session} />
              </div>
              <LiveryBuyButton
                session={session}
                livery={livery}
                className="mt-8 max-desktop-xs:hidden"
              />
            </div>
            <div className="col-span-full mt-5 flex items-center justify-between rounded bg-neutral-blue-800 px-[60px] py-8 max-desktop-xs:hidden max-desktop-xs:px-5">
              <p className="semibold-caps text-footer text-neutral-blue-500">
                Created By
              </p>
              <TrackedLink
                data-tracking-event={`Livery Profile Click`}
                trackingdata={{
                  type: 'livery page',
                  livery: livery,
                  user: livery.user,
                }}
                href={urlSafeProfilePath({
                  ...livery.user,
                  id: livery.userId,
                  profileType: 'livery',
                })}
                className="flex items-center gap-2"
              >
                <div className="relative flex h-8 w-8 overflow-hidden rounded-full">
                  <ImageWithFallback
                    src={getUserProfilePictureUrl(livery.user, {
                      profile: 'livery',
                    })}
                    id={livery.user.id}
                    fill
                  />
                </div>
                <p className="text-p-1 underline">
                  {getUserName({ user: livery.user }, { profile: 'livery' })}
                </p>
              </TrackedLink>
            </div>
          </div>
        </div>
      </div>

      {livery.descriptionHtml && (
        <div className="screen-grid mb-[96px] mt-[60px] desktop-xs:mt-24">
          <div className="col-span-full desktop-xs:col-span-5">
            <h2 className="text-h-5-sm desktop-xs:text-h-2">
              About this livery
            </h2>
            <div
              className={cn(
                htmlClassName,
                'mt-5 text-p-1 text-neutral-blue-500 desktop-xs:mt-8 desktop-xs:text-p-2',
              )}
              dangerouslySetInnerHTML={{
                __html: normalizeHtml(livery.descriptionHtml),
              }}
            />
          </div>
        </div>
      )}

      {!ownersLiveryDesignsLoading && ownersLiveryDesigns?.length > 1 && (
        <LiveryOwnersOtherDesigns
          {...{ ownersLiveryDesigns, livery, session }}
        />
      )}

      <div className="mt-12 bg-neutral-blue-900 desktop-xs:mt-[60px]">
        <div className="screen-grid">
          <LiveryDesignCTA
            session={session}
            className="relative col-span-full mx-auto flex h-full rounded-lg font-inter"
          />
          <div className="divide mt-[60px] desktop-xs:mt-[96px] desktop-xs:bg-neutral-blue-900"></div>
        </div>
      </div>
    </>
  )
}
